import { useEffect, useState, useCallback } from 'react';
import { useToast } from '@shared/ui';
import { postCart, putCart } from 'apis/cart';
import { getPromoProductGiveaway } from 'apis/promo';
import i18n from '@shared/i18n';
import { createAddToCartObj, createPromoProductData } from './utils';

interface UseGiveawaySheetModalPropTypes {
    visible: boolean;
}

export function useGiveawaySheetModal(props: UseGiveawaySheetModalPropTypes) {
    const { visible } = props;

    const toast = useToast();

    const [parentProduct, setParentProduct] = useState<ReturnType<
        typeof createAddToCartObj
    > | null>(null);

    const [selectedGiveawayProductMap, setSelectedGiveawayProductMap] = useState<
        Record<string, unknown>
    >({});

    const [promoProductPayloadMap, setPromoProductPayloadMap] = useState<
        Record<string, ReturnType<typeof createAddToCartObj>>
    >({});

    const [promoProductDetailMap, setPromoProductDetailMap] = useState<
        Record<string, FormattedBuyerPromoProduct>
    >({});

    const [selectedGiveawayProductSpecMap, setSelectedGiveawayProductSpecMap] = useState<
        Record<string, string[]>
    >({});

    const [promoProductDateInfo, setPromoProductDateInfo] =
        useState<PromoProductDateInfo>();

    const [isLoading, setLoading] = useState<boolean>(true);

    const [isFetching, setFetching] = useState<boolean>(true);

    const fetchGiveawayProducts = useCallback(
        async (product_id: string, quantity: number) => {
            const resp = await getPromoProductGiveaway({ product_id });

            if (resp && resp.data && parentProduct) {
                const { payload, detail } = createPromoProductData(
                    resp.data.products,
                    parentProduct
                );

                const { start_datetime, end_datetime, fixed_weekday, fixed_date } =
                    resp.data;

                setPromoProductDateInfo({
                    start_datetime,
                    end_datetime,
                    fixed_weekday,
                    fixed_date,
                });

                setPromoProductPayloadMap((prev) => {
                    // 繼承已選擇的數量
                    return Object.entries(payload).reduce((acc, curr) => {
                        const [key, value] = curr;

                        if (!Object.prototype.hasOwnProperty.call(prev, key)) {
                            acc[key] = value;

                            return acc;
                        }

                        const _quantity = Math.min(
                            acc[key]?.quantity || 1,
                            Number(detail[key].add_on_limit) * quantity
                        );

                        acc[key] = {
                            ...value,
                            ...acc[key],
                            quantity: _quantity,
                        };

                        return acc;
                    }, prev);
                });

                setPromoProductDetailMap(detail);
            }
        },
        [parentProduct]
    );

    function updateParentProductSpec(spec_detail_id: string) {
        setParentProduct((prev) => {
            if (!prev) {
                return prev;
            }

            return {
                ...prev,
                spec_detail_id,
            };
        });
    }

    function updateParentProduct(product: BuyerAddToCartPayload) {
        const parentProductObj = createAddToCartObj({
            product_id: product.product_id,
            quantity: product.quantity,
            ...(product.spec_detail_id ? { spec_detail_id: product.spec_detail_id } : {}),
        });

        setParentProduct(parentProductObj);
    }

    function updateParentProductCount(quantity: number) {
        setParentProduct((prev) => {
            if (!prev) {
                return prev;
            }

            return {
                ...prev,
                quantity,
            };
        });
    }

    function updatePromoProduct(id: string, quantity: number) {
        setPromoProductPayloadMap((prev) => {
            const _prev = prev;

            return {
                ..._prev,
                [id]: {
                    ..._prev[id],
                    quantity,
                },
            };
        });

        setSelectedGiveawayProductSpecMap((prev) => {
            const prevSpecs = prev[id] || [];

            if (prevSpecs.length > quantity) {
                // 如果新數量小於現有規格數量，則刪除多餘的規格
                return {
                    ...prev,
                    [id]: prevSpecs.slice(0, quantity),
                };
            }

            return prev;
        });
    }

    function updateSelectedGiveawayProduct(id: string) {
        setSelectedGiveawayProductMap((prev) => {
            if (Object.prototype.hasOwnProperty.call(prev, id)) {
                const { [id]: _, ...rest } = prev;

                return rest;
            }

            return {
                ...prev,
                [id]: 1,
            };
        });
    }

    function updateSelectedGiveawayProductSpec(
        id: string,
        index: number,
        spec_detail_id: string
    ) {
        setSelectedGiveawayProductSpecMap((prev) => {
            const prevSpecs = [...(prev[id] || [])];

            prevSpecs[index] = spec_detail_id;

            return {
                ...prev,
                [id]: prevSpecs,
            };
        });
    }

    async function addToCart(cartId: string | null) {
        if (!parentProduct) {
            throw new Error('Parent product is not set');
        }

        const giveawayProductList = Object.keys(selectedGiveawayProductMap).map((id) => {
            const promoProduct = promoProductPayloadMap[id];
            const selectedSpec = selectedGiveawayProductSpecMap[id];

            if (selectedSpec) {
                const specMap: Record<string, BuyerAddToCartPayload> = {};

                selectedSpec.forEach((specDetailId) => {
                    const key = `${promoProduct.product_id}-${promoProduct.promo_id}-${promoProduct.parent_product_id}-${specDetailId}`;

                    if (specMap[key]) {
                        specMap[key].quantity += 1;
                    } else {
                        specMap[key] = {
                            ...promoProduct,
                            quantity: 1,
                            spec_detail_id: specDetailId,
                        };
                    }
                });

                return Object.values(specMap);
            }

            return promoProduct || {};
        });

        const postPayload = [parentProduct].concat(...giveawayProductList);

        const putPayload = {
            quantity: parentProduct.quantity,
            giveaways: giveawayProductList.flat(),
        };

        let resp: PostCartResponse | null = null;

        if (cartId) {
            resp = await putCart(putPayload, cartId);
        } else {
            resp = await postCart(postPayload);
        }

        if (resp?.data) {
            toast({
                title: i18n.gettext('已加入購物車'),
                status: 'success',
            });
        }

        return resp;
    }

    function removeAllGiveawayProduct() {
        setSelectedGiveawayProductMap({});
    }

    const refetchGiveawayProducts = useCallback(() => {
        if (parentProduct && visible) {
            setFetching(true);
            fetchGiveawayProducts(
                parentProduct.product_id,
                parentProduct.quantity
            ).finally(() => {
                setLoading(false);
                setFetching(false);
            });
        }
    }, [parentProduct, visible, fetchGiveawayProducts]);

    function clearAllSelections() {
        setPromoProductPayloadMap({});
        setSelectedGiveawayProductMap({});
        setSelectedGiveawayProductSpecMap({});
        setLoading(true);
        setFetching(true);
    }

    useEffect(() => {
        refetchGiveawayProducts();
    }, [refetchGiveawayProducts]);

    const state = {
        parentProduct,
        promoProductDateInfo,
        promoProductPayloadMap,
        selectedGiveawayProductMap,
        promoProductDetailMap,
        isLoading,
        isFetching,
        selectedGiveawayProductSpecMap,
    };

    const actions = {
        addToCart,
        updatePromoProduct,
        updateParentProduct,
        updateParentProductSpec,
        updateSelectedGiveawayProduct,
        updateSelectedGiveawayProductSpec,
        removeAllGiveawayProduct,
        updateParentProductCount,
        refetchGiveawayProducts,
        clearAllSelections,
    };

    return {
        state,
        actions,
    };
}
